import React, { useRef, useEffect } from 'react';
import { PasswordField } from '~/components/organism/Forms';
import FormUtils from '~/components/organism/FormUtils';
import TEST_ID from './index.testid';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Input from '~/components/molecule/Input';
import PasswordRules, { type PasswordConditions } from '../PasswordRules';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';
import Validation from '~/util/Validation';

type Props = {
  submitting: boolean;
  passwordText?: string;
  repeatText?: string;
  passwordConditions: PasswordConditions;
  isFocused?: boolean;
  disabled?: boolean;
  labelColor?: ThemeColor;
  minimumCharacterLength?: number;
};

const text = {
  passwordLabel: 'Wachtwoord',
  repeatPasswordLabel: 'Herhaal wachtwoord',
};

const PasswordInputGroup: React.FCC<Props> = ({
  passwordConditions,
  submitting,
  passwordText,
  repeatText,
  isFocused,
  disabled,
  labelColor,
  minimumCharacterLength = Validation.Password.MINIMUM_PASSWORD_LENGTH,
}) => {
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isFocused) {
      if (passwordRef.current) {
        passwordRef.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <JustificationContainer width="100%" margin={['m', null]}>
        <PasswordField name="password">
          {({ input, meta: { error, touched } }) => (
            <Input
              {...input}
              width="100%"
              size="large"
              label={{
                text: passwordText ? passwordText : text.passwordLabel,
                color: labelColor,
              }}
              type="password"
              disabled={submitting || disabled}
              ref={passwordRef}
              externalErrors={
                error && touched
                  ? [FormUtils.showError(error, touched)]
                  : undefined
              }
              dataTestId={TEST_ID.PASSWORD_INPUT_FIELD}
            />
          )}
        </PasswordField>
      </JustificationContainer>

      <PasswordRules
        passwordConditions={passwordConditions}
        minimumCharacterLength={minimumCharacterLength}
      />
      <JustificationContainer width="100%" margin={['m', null]}>
        <PasswordField name="passwordRepeat">
          {({ input, meta: { error, touched } }) => (
            <Input
              {...input}
              width="100%"
              size="large"
              label={{
                text: repeatText ? repeatText : text.repeatPasswordLabel,
                color: labelColor,
              }}
              type="password"
              disabled={submitting || disabled}
              externalErrors={
                error && touched
                  ? [FormUtils.showError(error, touched)]
                  : undefined
              }
              dataTestId={TEST_ID.REPEAT_PASSWORD_INPUT_FIELD}
            />
          )}
        </PasswordField>
      </JustificationContainer>
    </>
  );
};

export default PasswordInputGroup;
